import { computed, type MaybeRefOrGetter } from 'vue'

import { useGroupsQueryStore } from '../stores/groups.query-store'
import { useGroupsUIStore } from '../stores/groups.store'

import type { CreateGroupInput } from '../services/groups.service'
import type { IGroup, IMembershipQuestion, NewsletterConfigToPatchAPI, UpdateGroupTabsConfigurationItem } from '../types/types'

// Main groups composable with TanStack Query integration
export const useGroups = () => {
  const queryStore = useGroupsQueryStore()
  const uiStore = useGroupsUIStore()

  // ===== QUERY CREATION FUNCTIONS =====
  // Following TanStack Query best practices: queries use fetch* prefix
  function fetchGroupsInfinite(params: MaybeRefOrGetter<{ limit?: number; search?: string; enabled?: boolean }>) {
    return queryStore.fetchGroupsInfiniteQuery(params)
  }
  function fetchGroupBySlug(slug: MaybeRefOrGetter<string>) {
    return queryStore.fetchGroupBySlugQuery(slug)
  }
  function fetchGroupById(groupId: MaybeRefOrGetter<string>) {
    return queryStore.fetchGroupByIdQuery(groupId)
  }
  function fetchPinnedGroups() {
    return queryStore.fetchPinnedGroupsQuery()
  }

  /**
   * Resolves the slug of the group a piece of content belongs to.
   * Feeds such as user profile contributions render content from groups other than the
   * routed/current one, so link building must resolve the slug from the content's groupId.
   * @param locationId Location of the content. Callers must pass the content's own locationId
   *   (not the active location) so the lookup stays scoped to the tenant that owns the group.
   */
  async function resolveGroupSlugById(groupId: string | undefined, locationId?: string): Promise<string | undefined> {
    if (!groupId) return undefined

    const currentGroup = uiStore.currentGroup
    if (currentGroup?._id === groupId && currentGroup.slug) {
      return currentGroup.slug
    }

    try {
      return await queryStore.fetchGroupSlugById(groupId, locationId)
    } catch (error) {
      console.error('[useGroups.resolveGroupSlugById] Failed to resolve group slug:', error)
      return undefined
    }
  }

  const fetchMembershipQuestionsForm = queryStore.fetchMembershipQuestionsFormQuery
  const fetchMembershipCustomFields = queryStore.fetchMembershipCustomFieldsQuery
  const fetchUserGroups = queryStore.fetchUserGroupsQuery
  const fetchDiscoveryGroups = queryStore.fetchDiscoveryGroupsQuery

  // ===== MUTATION INTERFACES =====
  // Following TanStack Query best practices: mutations use create*/update*/delete* prefix
  async function pinGroup(groupId: string) {
    // Check if already in progress
    if (queryStore.pinGroupMutation?.isPending?.value || queryStore.unpinGroupMutation?.isPending?.value) {
      console.log('Pin/unpin operation already in progress')
      return
    }

    try {
      return await queryStore.pinGroupMutation.mutateAsync(groupId)
    } catch (error) {
      console.error('Failed to pin group:', error)
      throw error
    }
  }

  async function unpinGroup(groupId: string) {
    // Check if already in progress
    if (queryStore.pinGroupMutation?.isPending?.value || queryStore.unpinGroupMutation?.isPending?.value) {
      console.log('Pin/unpin operation already in progress')
      return
    }

    try {
      return await queryStore.unpinGroupMutation.mutateAsync(groupId)
    } catch (error) {
      console.error('Failed to unpin group:', error)
      throw error
    }
  }

  async function leaveGroup(groupId: string, locationId?: string) {
    if (queryStore.leaveGroupMutation?.isPending?.value) {
      console.warn('[useGroups.leaveGroup] Leave group operation already in progress')
      return
    }

    try {
      await queryStore.leaveGroupMutation.mutateAsync({ groupId, locationId })
    } catch (error) {
      console.error('[useGroups.leaveGroup] Failed to leave group:', error)
      throw error
    }
  }

  function syncPricing(groupId: string) {
    return queryStore.syncPricingMutation.mutateAsync(groupId)
  }

  async function updateGroup(groupId: string, updates: Partial<CreateGroupInput>, locationId: string) {
    if (queryStore.updateGroupMutation?.isPending?.value) {
      console.warn('[useGroups.updateGroup] Update group operation already in progress')
      return
    }

    try {
      const result = await queryStore.updateGroupMutation.mutateAsync({
        groupId,
        updates,
        locationId,
      })
      if (result?._id && uiStore.currentGroup?._id === result._id) {
        uiStore.setCurrentGroup(result)
      }
      return result
    } catch (error) {
      console.error('[useGroups.updateGroup] Update failed:', error)
      throw error
    }
  }

  async function updateNewsletterConfig(groupId: string, locationId: string, config: NewsletterConfigToPatchAPI) {
    if (queryStore.updateNewsletterConfigMutation?.isPending?.value) {
      console.warn('[useGroups.updateNewsletterConfig] Update newsletter config operation already in progress')
      return
    }

    try {
      const result = await queryStore.updateNewsletterConfigMutation.mutateAsync({
        groupId,
        locationId,
        config,
      })
      if (result?._id && uiStore.currentGroup?._id === result._id) {
        uiStore.setCurrentGroup(result)
      }
      return result
    } catch (error) {
      console.error('[useGroups.updateNewsletterConfig] Update failed:', error)
      throw error
    }
  }

  async function updateGroupTabsConfiguration(
    groupId: string,
    locationId: string,
    tabsConfiguration: UpdateGroupTabsConfigurationItem[],
    groupSlug: string
  ): Promise<IGroup> {
    const result = await queryStore.updateGroupTabsConfigurationMutation.mutateAsync({
      groupId,
      locationId,
      tabsConfiguration,
      groupSlug,
    })
    if (result?._id && result._id === uiStore.currentGroup?._id) {
      uiStore.setCurrentGroup(result)
    }
    return result
  }

  async function toggleChatPreference(groupId: string, chatEnabled: boolean, locationId: string) {
    if (queryStore.toggleChatPreferenceMutation?.isPending?.value) {
      console.warn('[useGroups.toggleChatPreference] Toggle operation already in progress')
      return
    }

    try {
      await queryStore.toggleChatPreferenceMutation.mutateAsync({
        locationId,
        groupId,
        chatEnabled,
      })
    } catch (error) {
      console.error('[useGroups.toggleChatPreference] Toggle failed:', error)
      throw error
    }
  }

  // Utility function to create partial updates (only changed values)
  function createPartialUpdate<T extends Record<string, any>>(currentData: T, newData: Partial<T>, allowedFields: (keyof T)[]): Partial<T> {
    const updates: Partial<T> = {}

    for (const field of allowedFields) {
      //todo check and add deep equality check
      if (newData[field] !== undefined && newData[field] !== currentData[field]) {
        updates[field] = newData[field]
      }
    }

    return updates
  }

  const hasMediaId = (media: unknown): media is { id: string } =>
    typeof media === 'object' && media !== null && 'id' in media && typeof media.id === 'string'

  // ===== CENTRALIZED UPDATE METHODS =====

  /**
   * Updates the group media files
   * Centralized method to ensure consistent media updates across components
   */
  async function updateGroupMedia(groupId: string, media: unknown[], locationId: string) {
    try {
      const result = await updateGroup(groupId, { media }, locationId)
      return result
    } catch (error) {
      console.error('[useGroups.updateGroupMedia] Failed to update media:', error)
      throw error
    }
  }

  /**
   * Adds new media files to the group
   * Centralized method that gets current media and appends new files
   */
  async function addGroupMedia(groupId: string, newMedia: unknown[], locationId: string) {
    try {
      // Get current group data to append to existing media
      const currentGroup = uiStore.currentGroup
      if (!currentGroup) {
        throw new Error('Current group not found')
      }

      const existingMedia = currentGroup.media || []
      const updatedMedia = [...existingMedia, ...newMedia]

      const result = await updateGroupMedia(groupId, updatedMedia, locationId)
      return result
    } catch (error) {
      console.error('Failed to add media:', error)
      throw error
    }
  }

  /**
   * Removes media files from the group
   * Centralized method that filters out specified media files
   */
  async function removeGroupMedia(groupId: string, mediaToRemove: unknown[], locationId: string) {
    try {
      // Get current group data to filter out removed media
      const currentGroup = uiStore.currentGroup
      if (!currentGroup) {
        throw new Error('Current group not found')
      }

      const existingMedia = currentGroup.media || []
      const mediaIdsToRemove = mediaToRemove.filter(hasMediaId).map(media => media.id)
      const updatedMedia = existingMedia.filter(media => !hasMediaId(media) || !mediaIdsToRemove.includes(media.id))

      const result = await updateGroupMedia(groupId, updatedMedia, locationId)
      return result
    } catch (error) {
      console.error('Failed to remove media:', error)
      throw error
    }
  }

  /** @description Upload media file using the media service */
  async function uploadMediaFile(payload: {
    file: File
    filename: string
    folder: string
    type: string
    size: number
    locationId: string
    groupId: string
    progressUpdate?: (progress: number) => void
  }) {
    // Import MediaService dynamically to avoid circular dependencies
    const { default: MediaService } = await import('../services/media.service')
    return MediaService.uploadFile(payload)
  }

  // ===== BUSINESS LOGIC METHODS =====
  // async function fetchGroupsByLocation(query?: { limit?: number; search?: string }, force = false) {
  //   try {
  //     // Create an infinite query with the provided parameters
  //     console.log('auth fetchGroupsByLocation called', query)
  //     // if force is true, then reset the query
  //     const groupsQuery = await fetchGroupsInfinite(query || {})
  //     // Flatten all pages into a single array
  //     return groupsQuery?.allData || []
  //   } catch (error) {
  //     console.error('Failed to fetch groups:', error)
  //     throw error
  //   }
  // }

  // async function fetchPinnedGroupsData() {
  //   try {
  //     // Create the pinned groups query
  //     const pinnedQuery = fetchPinnedGroups()
  //     await pinnedQuery.suspense()
  //     return pinnedQuery.data.value || []
  //   } catch (error) {
  //     console.error('Failed to fetch pinned groups:', error)
  //     throw error
  //   }
  // }

  async function upsertMembershipQuestionsForm(groupId: string, locationId: string, questions: IMembershipQuestion[]) {
    return queryStore.upsertMembershipQuestionsFormMutation.mutateAsync({ groupId, locationId, questions })
  }

  function reset() {
    uiStore.reset()
  }

  function openJoinGroupModal() {
    uiStore.setShowJoinGroupModal(true)
  }
  function closeJoinGroupModal() {
    uiStore.setShowJoinGroupModal(false)
  }

  return {
    // Expose UI store for consumers
    store: uiStore,
    currentGroup: computed(() => uiStore.currentGroup),

    // Query creation functions (following fetch* naming convention)
    getGroups: fetchGroupsInfinite,
    fetchGroupBySlug,
    fetchGroupById,
    resolveGroupSlugById,
    getPinnedGroups: fetchPinnedGroups,
    fetchMembershipQuestionsForm,
    fetchMembershipCustomFields,
    fetchUserGroups,
    fetchDiscoveryGroups,

    // Mutation interfaces (following create*/update*/delete* naming convention)
    pinGroup,
    unpinGroup,
    leaveGroup,
    syncPricing,
    updateGroup,
    updateGroupTabsConfiguration,
    updateNewsletterConfig,
    upsertMembershipQuestionsForm,
    toggleChatPreference,
    createPartialUpdate,

    // Centralized update methods (recommended approach)
    updateGroupMedia,
    addGroupMedia,
    removeGroupMedia,
    uploadMediaFile,

    reset,

    // Join group modal control
    openJoinGroupModal,
    closeJoinGroupModal,
  }
}
